import React, { useMemo, useRef } from "react";
import styles from "./TimelineCanvas.module.css";
import {
  getScanTime,
  getStatusTime,
  colorByStatus,
  toHour,
  clamp,
  fmtTime,
} from "./timelineUtils";

export default function TimelineCanvas({
  receipts = [],
  selectedReceipt,
  onSelectReceipt,
  collapsedMode = "status",
  movementMode = "both",
  win,
  span,
  onWinChange,
}) {
  const drag = useRef({ active: false, startX: 0, rectW: 1, start: 0, end: 24 });

  // адаптивный шаг делений
  const pickTickMinutes = (span) => {
    if (span > 12) return 60;
    if (span > 6) return 30;
    if (span > 3) return 15;
    if (span > 1.5) return 10;
    if (span > 0.5) return 5;
    return 1;
  };

  const ticks = useMemo(() => {
    const step = pickTickMinutes(span);
    const startM = Math.ceil((win.start * 60) / step) * step;
    const endM = Math.floor((win.end * 60) / step) * step;
    const arr = [];
    for (let m = startM; m <= endM; m += step) {
      const h = m / 60;
      arr.push({
        h,
        label: `${String(Math.floor(h)).padStart(2, "0")}:${String(
          m % 60
        ).padStart(2, "0")}`,
      });
    }
    return arr;
  }, [win.start, win.end, span]);

  const toPct = (h) =>
    `${((clamp(h, win.start, win.end) - win.start) / (span || 1)) * 100}%`;

  /* === перетаскивание === */
  const onMouseDown = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    drag.current = {
      active: true,
      startX: e.clientX,
      rectW: rect.width,
      start: win.start,
      end: win.end,
    };
    document.body.style.cursor = "grabbing";
    document.body.style.userSelect = "none";
  };
  const onMouseMove = (e) => {
    if (!drag.current.active) return;
    const dx = e.clientX - drag.current.startX;
    const deltaH =
      (dx / drag.current.rectW) * (drag.current.end - drag.current.start);
    let start = drag.current.start - deltaH;
    let end = drag.current.end - deltaH;
    if (start < 0) {
      end -= start;
      start = 0;
    }
    if (end > 24) {
      start -= end - 24;
      end = 24;
    }
    onWinChange?.({ start, end });
  };
  const stopDrag = () => {
    drag.current.active = false;
    document.body.style.cursor = "";
    document.body.style.userSelect = "";
  };

  /* === Зум/панорамирование === */
const onWheel = (e) => {
  // Отключаем горизонтальное перемещение колесом
  // Разрешаем только зум при Shift/Ctrl/Meta
  if (e.shiftKey || e.ctrlKey || e.metaKey) {
    e.preventDefault();
    e.stopPropagation();

    const rect = e.currentTarget.getBoundingClientRect();
    const frac = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    const factor = 1 + Math.sign(e.deltaY) * 0.15;
    const newSpan = clamp(span * factor, 0.25, 24);
    const center = win.start + frac * span;
    let start = center - frac * newSpan;
    let end = start + newSpan;
    if (start < 0) {
      end -= start;
      start = 0;
    }
    if (end > 24) {
      start -= end - 24;
      end = 24;
    }
    onWinChange?.({ start, end });
  } else {
    // обычное колесо — ничего не делаем (запрещаем горизонтальный скролл)
    e.preventDefault();
  }
};


  return (
    <div
      className={styles.axisHost}
      onMouseDown={onMouseDown}
      onMouseMove={onMouseMove}
      onMouseUp={stopDrag}
      onMouseLeave={stopDrag}
      onWheel={onWheel}
    >
      {/* верхние деления */}
      <div className={styles.ticksRowLocal}>
        {ticks.map((t, i) => (
          <div key={i} className={styles.tickLocal} style={{ left: toPct(t.h) }}>
            {span > 1 || (i % 2 === 0) ? (
              <span className={styles.tickLabel}>{t.label}</span>
            ) : null}
            <span className={styles.vlineLocal} />
          </div>
        ))}
      </div>

      {/* строки таймлайна */}
      <div className={styles.timelineExpanded}>
        {receipts.map((r, idx) => {
          const scan = getScanTime(r);
          const stat = getStatusTime(r);
          const departed = r.departed_at ? new Date(r.departed_at) : null;
          const arrived = r.arrived_at ? new Date(r.arrived_at) : null;
          const nextDep = r.next_departure ? new Date(r.next_departure) : null;

          const hScan = scan ? toHour(scan) : null;
          const hStat = stat ? toHour(stat) : null;

          const leftH =
            hScan != null && hStat != null
              ? Math.min(hScan, hStat)
              : hScan ?? hStat ?? 0;
          const rightH =
            hScan != null && hStat != null
              ? Math.max(hScan, hStat)
              : leftH;
          const leftPct = toPct(leftH);
          const widthPct = `${
            ((clamp(rightH, win.start, win.end) - clamp(leftH, win.start, win.end)) /
              span) *
            100
          }%`;

          const isActive = selectedReceipt?.id === r.id;
          const cls = `${styles.recRow} ${isActive ? styles.activeRow : ""}`;

          return (
            <div key={r.id ?? idx} className={cls} onClick={() => onSelectReceipt?.(r)}>
              <div className={styles.rowTrack}>
                {r.hasTravel && (movementMode === "travel" || movementMode === "both") && (() => {
                  const hDep = toHour(r.departed);
                  const hArr = toHour(r.arrived);
                  const diffMin = Math.abs((r.arrived - r.departed) / 60000); // мин
                  const widthPct = ((hArr - hDep) / span) * 100;

                  if (diffMin < 1) {
                    return (
                      <div
                        className={styles.dot}
                        style={{
                          left: toPct((hDep + hArr) / 2),
                          backgroundColor: "#a855f7",
                          opacity: 0.7,
                        }}
                        title={`Короткое движение: ${fmtTime(r.departed)}–${fmtTime(r.arrived)}`}
                      />
                    );
                  }

                  return (
                    <div
                      className={styles.levelTravel}
                      style={{
                        left: toPct(Math.min(hDep, hArr)),
                        width: `${widthPct}%`,
                      }}
                      title={`В пути: ${fmtTime(r.departed)}–${fmtTime(r.arrived)}`}
                    />
                  );
                })()}

                {r.hasStop && (movementMode === "stop" || movementMode === "both") && (() => {
                  const hArr = toHour(r.arrived);
                  const hNext = toHour(r.next_departure);
                  const diffMin = Math.abs((r.next_departure - r.arrived) / 60000); // мин
                  const widthPct = ((hNext - hArr) / span) * 100;

                  if (diffMin < 1) {
                    return (
                      <div
                        className={styles.dot}
                        style={{
                          left: toPct((hArr + hNext) / 2),
                          backgroundColor: "#f59e0b",
                          opacity: 0.8,
                        }}
                        title={`Короткая стоянка: ${fmtTime(r.arrived)}–${fmtTime(r.next_departure)}`}
                      />
                    );
                  }

                  return (
                    <div
                      className={styles.levelStop}
                      style={{
                        left: toPct(Math.min(hArr, hNext)),
                        width: `${widthPct}%`,
                      }}
                      title={`Стоянка: ${fmtTime(r.arrived)}–${fmtTime(r.next_departure)}`}
                    />
                  );
                })()}


                {/* === скан/статус === */}
                {Math.abs(rightH - leftH) > 0.02 ? (
                  <div
                    className={styles.levelMain}
                    style={{
                      left: leftPct,
                      width: widthPct,
                      backgroundColor: colorByStatus(r.status),
                    }}
                    title={`Скан: ${fmtTime(scan)} | Статус: ${fmtTime(stat)}`}
                  />
                ) : (
                  <div
                    className={styles.dot}
                    style={{
                      left: toPct(hScan ?? hStat ?? 0),
                      backgroundColor: colorByStatus(r.status),
                    }}
                    title={`${collapsedMode === "scan" ? "Скан" : "Статус"}: ${fmtTime(
                      scan || stat
                    )}`}
                  />
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
